import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import type { TeachersMapPoint } from '../../services/adminAnalyticsApi';

type Props = {
  points: TeachersMapPoint[];
  height?: number;
};

const TeachersLocationMap: React.FC<Props> = ({ points, height = 360 }) => {
  const { teachers, clusters } = useMemo(() => {
    const t = points.filter((p) => p.type === 'teacher');
    const c = points.filter((p) => p.type === 'student_cluster');
    return { teachers: t, clusters: c };
  }, [points]);

  if (!points.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No geocodable postal codes yet. Add student/teacher postal codes to see the map.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        Approximate positions from French postal codes (department centroids). Bubble size = student count.
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="lng"
            name="Longitude"
            domain={[-5.5, 9.5]}
            tick={{ fontSize: 10 }}
            label={{ value: 'Longitude', position: 'insideBottom', offset: -2, fontSize: 10 }}
          />
          <YAxis
            type="number"
            dataKey="lat"
            name="Latitude"
            domain={[41.2, 51.2]}
            tick={{ fontSize: 10 }}
            label={{ value: 'Latitude', angle: -90, position: 'insideLeft', fontSize: 10 }}
          />
          <ZAxis type="number" dataKey="count" range={[40, 400]} />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            formatter={(value: number, name: string) => {
              if (name === 'count') return [value, 'Count'];
              return [value, name];
            }}
            labelFormatter={(_, payload) => {
              const p = payload?.[0]?.payload as TeachersMapPoint | undefined;
              if (!p) return '';
              const loc = [p.city, p.postalCode].filter(Boolean).join(' ');
              return `${p.label}${loc ? ` · ${loc}` : ''}`;
            }}
          />
          <Legend />
          <Scatter name="Teachers" data={teachers} fill="#1976d2" />
          <Scatter name="Student clusters" data={clusters} fill="#ed6c02" />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TeachersLocationMap;
